import React, { memo, useCallback, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import AutoStories from "@mui/icons-material/AutoStories";
import Quiz from "@mui/icons-material/Quiz";
import { useLocation, useNavigate } from "react-router-dom";

import type { SharedSidebarProps } from "src-next/shared/ui/layouts/SharedSidebar";
import type { SharedMenuItem } from "src-next/shared/ui/menuTypes";

import { ViewSwitcher } from "./ViewSwitcher";
import { VersionViewer } from "./VersionViewer";
import { useSessionContext } from "@/contexts/SessionContext";
import { currentFeatures } from "@/configs/Settings";

const DRAWER_WIDTH_EXPANDED = 260;
const DRAWER_WIDTH_COLLAPSED = 72;

function isLegacyMenuPathActive(
  itemPath: string | undefined,
  activePath: string,
): boolean {
  if (!itemPath) return false;
  if (activePath === itemPath) return true;
  return itemPath !== "/" && activePath.startsWith(`${itemPath}/`);
}

interface ExternalSidebarLinkItemProps {
  label: string;
  icon: ReactNode;
  isDesktop: boolean;
  isSidebarExpanded: boolean;
  disabled?: boolean;
  tooltipTitle?: string;
  onClick: () => void;
}

/**
 * ExternalSidebarLinkItem
 *
 * Links externos (Manual / Feedback) não são rotas internas, então não entram no
 * `pageRegistry`. Mesmo assim, precisam manter o MESMO padrão visual dos itens
 * de menu (padding, alinhamento do ícone e comportamento quando colapsado).
 *
 * Mantemos este componente pequeno e focado para reduzir risco e facilitar
 * padronização com o `LegacySidebarItem`.
 */
const ExternalSidebarLinkItem = memo<ExternalSidebarLinkItemProps>(
  ({
    label,
    icon,
    isDesktop,
    isSidebarExpanded,
    disabled = false,
    tooltipTitle,
    onClick,
  }) => {
    const mini = isDesktop && !isSidebarExpanded;

    return (
      <ListItem disablePadding>
        <Tooltip title={tooltipTitle ?? label} placement="right">
          <Box component="span" sx={{ display: "block", width: "100%" }}>
            <ListItemButton
              disabled={disabled}
              onClick={() => {
                if (disabled) return;
                onClick();
              }}
              sx={{
                p: 1,
                pl: 1,
                borderRadius: "8px",
                justifyContent: mini ? "center" : "flex-start",
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: mini ? 0 : 40,
                  justifyContent: "center",
                }}
              >
                {icon}
              </ListItemIcon>

              {(!mini || !isDesktop) && (
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{ noWrap: true }}
                />
              )}
            </ListItemButton>
          </Box>
        </Tooltip>
      </ListItem>
    );
  },
);

ExternalSidebarLinkItem.displayName = "ExternalSidebarLinkItem";

interface LegacySidebarItemProps {
  item: SharedMenuItem;
  level: number;
  isDesktop: boolean;
  isSidebarExpanded: boolean;
  activePath: string;
  onNavigate: (item: SharedMenuItem) => void;
  isGroupOpen: boolean;
  toggleGroup: () => void;
  forceDisabled?: boolean;
  tooltipTitle?: string;
}

const LegacySidebarItem = memo<LegacySidebarItemProps>(
  ({
    item,
    level,
    isDesktop,
    isSidebarExpanded,
    activePath,
    onNavigate,
    isGroupOpen,
    toggleGroup,
    forceDisabled = false,
    tooltipTitle,
  }) => {
    const hasChildren = Boolean(item.children && item.children.length > 0);
    /**
     * Item ativo:
     * - path === activePath, ou prefixo (ex.: /ecommerce/gerenciar/...)
     * - Grupo: ativo também na rota do hub (ex.: "/settings").
     */
    const isActive = isLegacyMenuPathActive(item.path, activePath);

    const isChildActive = Boolean(
      hasChildren &&
      item.children?.some((child) =>
        isLegacyMenuPathActive(child.path, activePath),
      ),
    );

    const isDisabled = Boolean(item.disabled) || forceDisabled;

    const handleClick = () => {
      if (isDisabled) return;

      if (hasChildren) {
        toggleGroup();
        if (item.path) {
          onNavigate(item);
        }
        return;
      }

      if (item.path) {
        onNavigate(item);
      }
    };

    const paddingLeft = useMemo(() => {
      if (!isDesktop || !isSidebarExpanded) return 1;
      // Aproxima o visual do legado (SectionSideBar usa pl 4 para subitens)
      return level === 0 ? 1 : 4;
    }, [isDesktop, isSidebarExpanded, level]);

    const mini = isDesktop && !isSidebarExpanded;

    return (
      <>
        <Tooltip title={tooltipTitle ?? item.label} placement="right">
          <Box component="span" sx={{ display: "block" }}>
            <ListItemButton
              disabled={isDisabled}
              selected={isActive}
              onClick={handleClick}
              sx={{
                p: 1,
                pl: paddingLeft,
                borderRadius: "8px",
                justifyContent: mini ? "center" : "flex-start",
                backgroundColor:
                  isActive || isChildActive ? "neutral200.main" : undefined,
                "&:hover": {
                  backgroundColor:
                    isActive || isChildActive ? "neutral300.main" : undefined,
                },
              }}
            >
              {item.icon && (
                <ListItemIcon
                  sx={{
                    minWidth: mini ? 0 : 40,
                    justifyContent: "center",
                  }}
                >
                  {item.icon as ReactNode}
                </ListItemIcon>
              )}

              {(!mini || !isDesktop) && (
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ noWrap: true }}
                />
              )}

              {hasChildren && (!mini || !isDesktop) && (
                <Box sx={{ ml: "auto" }}>
                  {isGroupOpen ? <ExpandLess /> : <ExpandMore />}
                </Box>
              )}
            </ListItemButton>
          </Box>
        </Tooltip>

        {hasChildren && (!mini || !isDesktop) && (
          <Collapse in={isGroupOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map((child) => (
                <LegacySidebarItem
                  key={child.id}
                  item={child}
                  level={level + 1}
                  isDesktop={isDesktop}
                  isSidebarExpanded={isSidebarExpanded}
                  activePath={activePath}
                  onNavigate={onNavigate}
                  isGroupOpen
                  toggleGroup={() => undefined}
                  forceDisabled={forceDisabled}
                  tooltipTitle={tooltipTitle}
                />
              ))}
            </List>
          </Collapse>
        )}
      </>
    );
  },
);

LegacySidebarItem.displayName = "LegacySidebarItem";

/**
 * LegacySharedSidebar
 *
 * Sidebar V2 do legado, plugável no `LayoutColapse`.
 *
 * Requisitos atendidos:
 * - Drawer temporário (mobile) e permanente (desktop), como `SharedSidebar`
 * - Colapso em desktop (mini) via `expanded`
 * - Sem o cabeçalho interno "Menu" (trecho removido do padrão)
 * - Estilo dos itens e scroll alinhado ao legado (SideBarMainContent/SectionSideBar)
 * - Rodapé com `ViewSwitcher` + `VersionViewer` (substitui assinatura digital)
 */
const LegacySharedSidebar = memo<SharedSidebarProps>(
  ({ items, expanded, setExpanded, container }) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
    const navigate = useNavigate();
    const location = useLocation();
    const { access } = useSessionContext();
    const isRegisterMode = currentFeatures.register;
    const registerTooltipTitle = "Disponível após concluir o cadastro";

    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

    const drawerWidth = isDesktop
      ? expanded
        ? DRAWER_WIDTH_EXPANDED
        : DRAWER_WIDTH_COLLAPSED
      : DRAWER_WIDTH_EXPANDED;

    const handleNavigate = useCallback(
      (item: SharedMenuItem) => {
        if (!item.path) return;
        if (isRegisterMode) return;

        navigate(item.path, { state: item.navigationState });

        // Em mobile, fecha após navegação.
        if (!isDesktop) {
          setExpanded(false);
        }
      },
      [isDesktop, isRegisterMode, navigate, setExpanded],
    );

    const handleToggleGroup = useCallback((id: string) => {
      setOpenGroups((previous) => ({
        ...previous,
        [id]: !(previous[id] ?? false),
      }));
    }, []);

    const drawerContent = (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Spacer do AppBar fixo (LayoutColapse adiciona Toolbar no main, mas a sidebar também precisa) */}
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, flexShrink: 0 }} />

        {/* Lista com scroll e scrollbar do legado */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            px: expanded || !isDesktop ? 1 : 0.5,
            py: 1,
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "primary700.main",
            },
          }}
        >
          <List sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {items.map((item) => {
              const isPathActive = isLegacyMenuPathActive(
                item.path,
                location.pathname,
              );
              const isAnyChildActive = Boolean(
                item.children?.some((child) =>
                  isLegacyMenuPathActive(child.path, location.pathname),
                ),
              );

              // Default colapsado, mas abre automaticamente quando a seção está ativa.
              const isGroupOpen =
                openGroups[item.id] ?? (isPathActive || isAnyChildActive);
              return (
                <LegacySidebarItem
                  key={item.id}
                  item={item}
                  level={0}
                  isDesktop={isDesktop}
                  isSidebarExpanded={expanded}
                  activePath={location.pathname}
                  onNavigate={handleNavigate}
                  isGroupOpen={isGroupOpen}
                  toggleGroup={() => handleToggleGroup(item.id)}
                  forceDisabled={isRegisterMode}
                  tooltipTitle={isRegisterMode ? registerTooltipTitle : item.label}
                />
              );
            })}

            {/* Links externos do legado (antes ficavam em SideBarMainContent) */}
            <ExternalSidebarLinkItem
              label="Manual"
              icon={<AutoStories />}
              isDesktop={isDesktop}
              isSidebarExpanded={expanded}
              disabled={isRegisterMode}
              tooltipTitle={isRegisterMode ? registerTooltipTitle : "Manual"}
              onClick={() =>
                window.open(
                  "https://projectcore.atlassian.net/wiki/spaces/FlowiseFluxmed/overview",
                  "_blank",
                )
              }
            />

            <ExternalSidebarLinkItem
              label="Feedback e Suporte"
              icon={<Quiz />}
              isDesktop={isDesktop}
              isSidebarExpanded={expanded}
              disabled={isRegisterMode}
              tooltipTitle={
                isRegisterMode ? registerTooltipTitle : "Feedback e Suporte"
              }
              onClick={() =>
                window.open(
                  access?.type === "professional"
                    ? "https://projectcore.atlassian.net/servicedesk/customer/portal/11"
                    : "https://projectcore.atlassian.net/servicedesk/customer/portal/12",
                  "_blank",
                )
              }
            />
          </List>
        </Box>

        <Divider />

        {/* Rodapé: substitui a seção de assinatura digital do SharedSidebar */}
        <Box sx={{ flexShrink: 0 }}>
          {isDesktop && (
            <>
              <ViewSwitcher isDrawerOpen={!isDesktop || expanded} />
              <Divider />
            </>
          )}
          <VersionViewer />
        </Box>
      </Box>
    );

    return (
      <>
        {/* Drawer temporário (mobile) */}
        <Drawer
          container={container}
          variant="temporary"
          open={!isDesktop && expanded}
          onClose={() => setExpanded(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH_EXPANDED,
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Drawer permanente (desktop) */}
        <Box
          component="nav"
          sx={{
            width: isDesktop ? drawerWidth : 0,
            flexShrink: 0,
            display: { xs: "none", md: "block" },
          }}
          aria-label="menu de navegação"
        >
          <Drawer
            variant="permanent"
            open={isDesktop}
            sx={{
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
                overflowX: "hidden",
              },
            }}
          >
            {drawerContent}
          </Drawer>
        </Box>
      </>
    );
  },
);

LegacySharedSidebar.displayName = "LegacySharedSidebar";

export default LegacySharedSidebar;
